import { NgFor, NgIf } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  Output,
  ViewEncapsulation,
} from '@angular/core';
import { FormGroup, ReactiveFormsModule } from '@angular/forms';
import { MatExpansionModule } from '@angular/material/expansion';
import { TranslateModule } from '@ngx-translate/core';
import { ConfigurationService } from '@sc/data-access-configuration';
import {
  AnswerChoice,
  canRepairImpactsType,
  Glass,
  GlassDisplayElement,
  GlassExtended,
  GlassExtendedOnlyEnum,
  GlassQuestion,
} from '@sc/data-access-models';
import { TranslatorService } from '@sc/data-access-translation';
import { FeatureFlippingManagerService } from '@sc/feature-flipping';
import {
  AsteriskInfoComponent,
  LabelRequiredPipe,
  SelectComponent,
} from '@sc/ui-components';
import { BehaviorSubject } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { GlassChoiceAccordion } from '../glass-choice-accordion/glass-choice-accordion.component';

@Component({
  selector: 'sc-sinister-declaration-accordion-panel',
  styleUrls: ['./sinister-declaration-accordion-panel.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    NgIf,
    MatExpansionModule,
    NgFor,
    GlassChoiceAccordion,
    ReactiveFormsModule,
    LabelRequiredPipe,
    TranslateModule,
    SelectComponent,
    AsteriskInfoComponent,
  ],
  template: `
    <div *ngIf="parentForm">
      <mat-accordion>
        <sc-asterisk-info></sc-asterisk-info>
        <ng-container *ngFor="let index of [0, 1, 2, 3, 4]">
          <sc-glass-choice-accordion
            *ngIf="displayQuestion(index)"
            [numberQuestion]="getNumberQuestion(index)"
            [glass]="parentForm.get('glass')?.value"
            [answer]="getAnswer(index)"
            [glassQuestion]="getQuestion(index)"
            [isExpanded]="index === questionExpanded"
            [reInitExpansion]="reInitExpansion"
            [displayQuestionIcons]="
              featureFlippingManager.isFeatureActivated(
                'b2b-display-sinister-icons'
              )
            "
            [isQuestionError]="isFormError && !answeredQuestion(index)"
            (answerChangeEvent)="onAnswerChange(index, $event)"
          ></sc-glass-choice-accordion>
        </ng-container>
      </mat-accordion>

      <mat-accordion multi [formGroup]="parentForm">
        <!-- #############
               Comment panel
               #############-->
        <mat-expansion-panel
          *ngIf="
            this.featureFlippingManager.isFeatureActivated(
              'b2b-can-declare-sinister-comment'
            )
          "
          #SinisterComment
          [expanded]="true"
        >
          <mat-expansion-panel-header>
            <mat-panel-title>
              <!-- Expanded title -->
              <div *ngIf="SinisterComment.expanded">
                <p
                  class="question-title  sc-design-caption"
                  [innerHTML]="
                    'SinisterDataForm.commentTitle' | labelRequired: false
                  "
                ></p>
                <p class="question-text sc-design-text-4-bold">
                  {{ 'SinisterDataForm.commentBody' | translate }}
                </p>
              </div>
              <!-- Not Expanded title -->
              <div *ngIf="!SinisterComment.expanded">
                <p
                  class="closed-title sc-design-caption"
                  [innerHTML]="
                    'SinisterDataForm.commentTitle' | labelRequired: false
                  "
                ></p>
              </div>
            </mat-panel-title>
          </mat-expansion-panel-header>
          <textarea
            formControlName="sinisterComment"
            (ngModelChange)="onCommentChange($event)"
          ></textarea>
        </mat-expansion-panel>

        <!-- ###############
             Franchise panel
             ###############-->
        <mat-expansion-panel
          *ngIf="showExcessInput()"
          #SinisterExcess
          [expanded]="true"
        >
          <mat-expansion-panel-header>
            <mat-panel-title>
              <!-- Expanded title -->
              <div *ngIf="SinisterExcess.expanded">
                <p
                  class="question-title sc-design-caption"
                  [innerHTML]="
                    'SinisterDataForm.excessTitle' | labelRequired: false
                  "
                ></p>
                <p class="question-text sc-design-text-4-bold">
                  {{ 'SinisterDataForm.excessBody' | translate }}
                </p>
              </div>
              <!-- Not Expanded title -->
              <div *ngIf="!SinisterExcess.expanded">
                <p
                  class="closed-title"
                  [innerHTML]="
                    'SinisterDataForm.excessTitle' | labelRequired: false
                  "
                ></p>
                <p class="closed-text sc-design-text-4-bold">
                  {{ getSelectedExcessValue() }}
                </p>
              </div>
            </mat-panel-title>
          </mat-expansion-panel-header>

          <sc-select
            [value]="this.parentForm.get('sinisterExcess')?.value"
            [options]="excessOptions"
            class="excess-select"
            [placeholder]="getExcessDefaultValue()"
            (valueChange)="onExcessSelect($event)"
          >
            <ng-template #optionTemplate let-option>
              {{ option }} €
            </ng-template>
            <ng-template #triggerTemplate let-option>
              {{ option }} €
            </ng-template>
          </sc-select>
        </mat-expansion-panel>
      </mat-accordion>
    </div>
  `,
})
export class SinisterDeclarationAccordionPanelComponent {
  @Input() parentForm: FormGroup;
  @Input() set isReparation(value: boolean) {
    this._isReparation = value;
    if (
      this.featureFlippingManager.isFeatureActivated(
        'b2b-hide-excess-if-reparation'
      ) &&
      value === true
    ) {
      this.onExcessSelect('');
    }
  }
  @Input() set updateDisplay(changeValue: boolean) {
    this.init();
  }
  @Input() isFormError: boolean;
  @Output() glassChangeEvent = new EventEmitter<AnswerChoice>();
  @Output() commentChangeEvent = new EventEmitter<void>();
  @Output() excessChangeEvent = new EventEmitter<void>();

  commentChange$ = new BehaviorSubject<string>(null);
  _isReparation: boolean;
  isDisplayUpdated = true;

  treeDisplay: GlassDisplayElement[];

  questionExpanded: number;
  reInitExpansion = false;
  excessOptions: number[];

  constructor(
    public featureFlippingManager: FeatureFlippingManagerService,
    public translatorService: TranslatorService,
    private readonly changeDetectorRef: ChangeDetectorRef,
    public configService: ConfigurationService
  ) {
    this.commentChange$
      .pipe(debounceTime(250), distinctUntilChanged())
      .subscribe(() => {
        this.commentChangeEvent.emit();
      });
    this.treeDisplay = configService.getGlassUsage().treeDisplay;
  }

  /**
   * Inits sinister declaration accordion panel component
   */
  init() {
    this.questionExpanded = -1;
    for (let index = 4; index >= 0; index--) {
      if (this.displayQuestion(index) && !this.answeredQuestion(index)) {
        this.questionExpanded = index;
        break;
      }
    }
    this.reInitExpansion = !this.reInitExpansion;
    this.changeDetectorRef.detectChanges();
    this.excessOptions = this.configService
      .getAllExcess()
      .map((excess) => parseInt(excess?.match(/\d+/)?.[0]));
  }

  getNumberQuestion(index: number): number {
    if (index === 0) {
      return 1;
    }
    return (
      this.getNumberQuestion(index - 1) + (this.displayQuestion(index) ? 1 : 0)
    );
  }

  displayQuestion(index: number): boolean {
    if (index === 0) {
      return true;
    }
    if (!this.answeredQuestion(index - 1)) {
      return false;
    }

    if (this.parentForm.get('glass')?.value === 'windshield') {
      if (index === 1) {
        return true;
      }

      switch (this.getQuestion(index - 1)?.key) {
        case 'impactsType':
          return canRepairImpactsType(
            this.parentForm.get('impacts')?.get('impactsType')?.value
          );
        case 'size':
          return (
            this.parentForm.get('impacts')?.get('size')?.value === 'smaller2cm'
          );
        case 'inVisionField':
          return (
            this.parentForm.get('impacts')?.get('inVisionField')?.value ===
            false
          );
        default:
          // pas censé pouvoir arriver
          return false;
      }
    }
    return this.getQuestion(index)?.values.length > 1;
  }

  getQuestion(index: number, nextAnswers?: AnswerChoice): GlassQuestion {
    if (index === 0) {
      return {
        key: 'glass',
        values: this.treeDisplay.map(
          (glass) => glass.glassExtended || glass.glass
        ),
      };
    }
    const glass = nextAnswers
      ? nextAnswers.glass
      : this.parentForm.get('glass')?.value;
    const glassExtended = nextAnswers
      ? nextAnswers.glassExtended
      : this.parentForm.get('glassExtended')?.value;
    return this.treeDisplay.find(
      (glassConfig) =>
        glass === glassConfig.glass &&
        (!glassExtended || glassExtended === glassConfig.glassExtended)
    )?.sub?.[index - 1];
  }

  getAnswer(index: number): string | boolean {
    const questionKey = this.getQuestion(index)?.key;
    if (questionKey === 'glass')
      return (
        this.parentForm.get('glassExtended')?.value ||
        this.parentForm.get('glass')?.value
      );
    switch (this.parentForm.get('glass')?.value as Glass) {
      case 'windshield':
        return this.parentForm.get('impacts')?.get(questionKey)?.value;
      case 'sideWindow':
        return this.parentForm.get('lateralPrecisions')?.get(questionKey)
          ?.value;
      case 'mirror':
        return this.parentForm.get('mirror')?.value;
      case 'lights':
        return this.parentForm.get('light')?.get(questionKey)?.value;
      default:
        break;
    }
  }

  answeredQuestion(index: number): boolean {
    const answer = this.getAnswer(index);
    return answer !== undefined && answer !== null;
  }

  onAnswerChange(
    index: number,
    event: { answer: string | boolean; isDisplayUpdated: boolean }
  ): AnswerChoice {
    let glass: Glass;
    let glassExtended: GlassExtended;
    const primaryAnswer = (
      index === 0 ? event.answer : this.getAnswer(0)
    ) as GlassExtended;
    if (
      Object.values(GlassExtendedOnlyEnum).includes(
        primaryAnswer as GlassExtendedOnlyEnum
      )
    ) {
      glass = this.treeDisplay.find(
        (glassDisplayElement) =>
          !!glassDisplayElement.glassExtended &&
          glassDisplayElement.glassExtended === primaryAnswer
      )?.glass;
      glassExtended = primaryAnswer;
    } else {
      glass = primaryAnswer as Glass;
      glassExtended = null;
    }
    const answers: AnswerChoice = {
      glass: glass,
      glassExtended: glassExtended,
      isDisplayUpdated: event.isDisplayUpdated,
      sub: {},
    };
    for (let i = 1; i < index; i++) {
      answers.sub[this.getQuestion(i).key] = this.getAnswer(i);
    }

    if (event.answer !== this.getAnswer(index)) {
      if (index !== 0) {
        answers.sub[this.getQuestion(index).key] = event.answer;
      }
      let i = index + 1;
      let questionI = this.getQuestion(i, answers);
      while (!!questionI && questionI.values.length === 1) {
        answers.sub[questionI.key] =
          typeof questionI.values[0] === 'object'
            ? questionI.values[0].value
            : questionI.values[0];
        i++;
        questionI = this.getQuestion(i, answers);
      }
      this.glassChangeEvent.emit(answers);
      this.changeDetectorRef.detectChanges();
      return;
    }

    for (let i = index; i <= 5; i++) {
      if (!this.answeredQuestion(i)) {
        break;
      }
      if (i > 0) {
        answers.sub[this.getQuestion(i).key] = this.getAnswer(i);
      }
    }
    this.glassChangeEvent.emit(answers);
    this.changeDetectorRef.detectChanges();
  }

  onExcessSelect(newExcess: string) {
    this.parentForm.get('sinisterExcess').setValue(newExcess);
    this.excessChangeEvent.emit();
  }

  onCommentChange(newComment: string) {
    this.commentChange$.next(newComment);
  }

  showExcessInput() {
    return (
      this.featureFlippingManager.isFeatureActivated(
        'b2b-can-declare-excess'
      ) &&
      (!this.featureFlippingManager.isFeatureActivated(
        'b2b-hide-excess-if-reparation'
      ) ||
        !this._isReparation)
    );
  }

  getSelectedExcessValue() {
    return (
      this.parentForm.get('sinisterExcess').value ||
      this.configService.getConfig().excessOptions.defaultExcess
    );
  }

  getExcessDefaultValue() {
    return this.configService.getConfig().excessOptions.defaultExcess;
  }
}
